import React, { useState, useEffect } from 'react';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import { AllianceQuoteRequest, QuoteRequest } from '../../types/quote';
import { Trans, useTranslation } from 'react-i18next';
import { useQuoteStore } from '../../stores/quoteStore';
import { QuoteJobInfo } from '../quotes/QuoteJobInfo';
import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import ReportModal from '../reports/ReportModal';
import WalletSummary from '../credits/WalletSummary';
import { useCreditStore } from '../../stores/creditStore';
import { useNavigate } from 'react-router-dom';

interface QuoteRequestRespondModalProps {
  quoteRequest: QuoteRequest | AllianceQuoteRequest;
  onClose: () => void;
}

interface QuoteAcceptContentProps {
  quoteRequest: QuoteRequest | AllianceQuoteRequest;
  onDecline: () => void;
  onClose: () => void;
}

const QuoteAcceptContent = ({ quoteRequest, onDecline, onClose }: QuoteAcceptContentProps) => {
  const { t } = useTranslation('prospects');
  const { acceptQuoteRequest, acceptAllianceQuoteRequest, createQuote } = useQuoteStore();
  const { balance, getActionCost, refreshBalance, fetchActionCosts, actionCosts } =
    useCreditStore();

  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptSuccess, setAcceptSuccess] = useState(false);

  const navigate = useNavigate();

  const isAllianceQuoteRequest = 'parent_quote_owner' in quoteRequest;

  const sourceProfile = isAllianceQuoteRequest
    ? quoteRequest.parent_quote_owner
    : quoteRequest.job.client;

  const clientName = sourceProfile.first_name;

  // Get the cost for accepting quote requests
  const acceptQuoteRequestCost = getActionCost('accept_quote_request');
  const acceptAllianceQuoteRequestCost = getActionCost('accept_quote_alliance_request');
  const requiredCost = isAllianceQuoteRequest
    ? acceptAllianceQuoteRequestCost
    : acceptQuoteRequestCost;

  // Check if user has enough credits
  const hasEnoughCredits = balance !== null && balance >= requiredCost;

  useEffect(() => {
    refreshBalance();
    // Fetch action costs if not already loaded
    if (!Object.keys(actionCosts).length) {
      fetchActionCosts();
    }
  }, [actionCosts, fetchActionCosts, refreshBalance]);

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError(null);
    try {
      if (isAllianceQuoteRequest) {
        await acceptAllianceQuoteRequest(quoteRequest.id);
      } else {
        await acceptQuoteRequest(quoteRequest.id);
      }

      await createQuote({
        jobId: quoteRequest.job.id,
        parentQuoteId: quoteRequest.parent_quote_id ?? undefined,
      });
      // Refresh balance after successful acceptance
      await refreshBalance();
      setAcceptSuccess(true);
    } catch (error) {
      console.error(error);
      const errorMessage = (error as { message: string }).message;
      if (errorMessage.includes('INSUFFICIENT_CREDITS_ERROR')) {
        setAcceptError(
          t(
            'quoteRequestRespondModal.insufficientCredits',
            'Crédits insuffisants pour accepter cette demande. Veuillez acheter des crédits.'
          )
        );
        await refreshBalance();
      } else if (errorMessage.includes('JOB_NOT_AVAILABLE')) {
        setAcceptError(
          t(
            'quoteRequestRespondModal.jobNotAvailable',
            "Ce projet n'est plus disponible. Vous ne pouvez plus accepter cette demande."
          )
        );
      } else {
        setAcceptError(t('quoteRequestRespondModal.acceptError', "Erreur lors de l'acceptation"));
      }
    } finally {
      setAccepting(false);
    }
  };

  if (acceptSuccess) {
    return <QuoteAcceptSuccessContent quoteRequest={quoteRequest} onClick={onClose} />;
  }

  const canRespondQuoteRequest = quoteRequest.status === 'pending';

  const getQuoteRequestModalTitle = (quoteStatus: string) => {
    if (quoteStatus === 'accepted') {
      return t('quoteRequestRespondModal.acceptedTitle', 'Demande de soumission acceptée');
    } else if (quoteStatus === 'rejected') {
      return t('quoteRequestRespondModal.declinedTitle', 'Demande de soumission rejetée');
    }
    return t('quoteRequestRespondModal.acceptTitle', 'Accepter une demande de soumission');
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-secondary">
        {getQuoteRequestModalTitle(quoteRequest.status)}
      </h2>
      <div className="text-base text-secondary">
        {t('quoteRequestRespondModal.clientName')} <span className="font-bold">{clientName}</span>
      </div>
      {quoteRequest.request_message && (
        <>
          <hr className="border-[#E1E9F0]" />
          <div className="text-base text-secondary">
            <h3 className="font-bold">{t('quoteRequestRespondModal.requestMessageLabel')}</h3>
            <p className="mt-1 whitespace-pre-wrap">{quoteRequest.request_message}</p>
          </div>
        </>
      )}

      <hr className="border-[#E1E9F0]" />
      <QuoteJobInfo quoteJob={quoteRequest.job} showProfileAvatar={true} />
      {acceptError && (
        <div className="font-ambit mt-1 text-[0.9375rem] text-[#F04438]">{acceptError}</div>
      )}

      <div className="mt-8 flex flex-col gap-4 md:mt-4 md:flex-row md:flex-row-reverse md:flex-wrap md:items-center md:justify-between md:gap-8">
        <div className="flex flex-col gap-4 md:flex-row md:flex-row-reverse md:items-center">
          {canRespondQuoteRequest && (
            <>
              <Button
                type="button"
                onClick={handleAccept}
                className="w-full md:w-auto md:px-8"
                disabled={accepting || !hasEnoughCredits}
              >
                {accepting
                  ? t('quoteRequestRespondModal.acceptingButton', 'Acceptation...')
                  : t('quoteRequestRespondModal.acceptButton', {
                      count: requiredCost,
                    })}
              </Button>
              <Button
                type="button"
                onClick={onDecline}
                className="w-full md:w-auto md:px-8"
                variant="transparent-with-border"
              >
                {t('quoteRequestRespondModal.declineButton')}
              </Button>
            </>
          )}
          {quoteRequest.status === 'accepted' && (
            <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
              <Button
                className="w-full md:w-auto md:px-8"
                variant="primary"
                onClick={() => {
                  navigate('/quotes', { state: { selected_id: quoteRequest.job.id } });
                }}
              >
                {t('quoteRequestRespondModal.continueQuote', 'Continuer ma soumission')}
              </Button>
              <Button
                className="w-full md:w-auto md:px-8"
                variant="transparent-with-border"
                onClick={() => {
                  navigate('/prospects', { state: { selected_id: quoteRequest.job.client.id } });
                  onClose();
                }}
              >
                {t('quoteRequestRespondModal.showNewContact', 'Voir mon nouveau contact')}
              </Button>
            </div>
          )}
        </div>
        {canRespondQuoteRequest && (
          <WalletSummary
            outerClassName="w-full md:w-full md:max-w-[18rem]"
            modalParams={{
              type: 'quoteRequest',
              quoteRequestId: quoteRequest.id,
            }}
          />
        )}
      </div>
      {canRespondQuoteRequest && !hasEnoughCredits && (
        <div className="mt-2 text-xs text-secondary opacity-70">
          {t(
            'quoteRequestRespondModal.insufficientCreditsNote',
            `${requiredCost} jeton(s) requis pour accepter cette demande.`
          )}
        </div>
      )}
      {canRespondQuoteRequest && hasEnoughCredits && (
        <div className="mt-2 text-right text-xs text-secondary opacity-70">
          {t('quoteRequestRespondModal.creditNote')}
        </div>
      )}
    </>
  );
};

const QuoteAcceptSuccessContent = ({
  quoteRequest,
  onClick,
}: {
  quoteRequest: QuoteRequest | AllianceQuoteRequest;
  onClick?: () => void;
}) => {
  const { t } = useTranslation('prospects');
  const sourceProfile =
    'parent_quote_owner' in quoteRequest
      ? quoteRequest.parent_quote_owner
      : quoteRequest.job.client;
  const clientName = sourceProfile.first_name;

  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold text-secondary">
        {t('quoteRequestRespondModal.acceptedTitle', 'Demande acceptée')}
      </h2>
      <p className="mb-4 text-secondary">
        <Trans
          i18nKey={'quoteRequestRespondModal.acceptedMessage'}
          ns={'prospects'}
          values={{ requesterName: clientName }}
          components={{ bold: <span className="font-bold" /> }}
        />
      </p>
      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
        <Button
          className="w-full md:w-auto md:px-8"
          variant="primary"
          onClick={() => {
            navigate('/quotes', { state: { selected_id: quoteRequest.job.id } });
          }}
        >
          {t('quoteRequestRespondModal.startQuote', 'Commencer ma soumission')}
        </Button>
        <Button
          className="w-full md:w-auto md:px-8"
          variant="transparent-with-border"
          onClick={() => {
            navigate('/prospects', { state: { selected_id: quoteRequest.job.client.id } });
            onClick?.();
          }}
        >
          {t('quoteRequestRespondModal.showNewContact', 'Voir mon nouveau contact')}
        </Button>
      </div>
    </div>
  );
};

interface QuoteDeclineContentProps {
  quoteRequest: QuoteRequest | AllianceQuoteRequest;
  onBack: () => void;
  onClick?: () => void;
}

const QuoteDeclineContent = ({ quoteRequest, onBack, onClick }: QuoteDeclineContentProps) => {
  const { t } = useTranslation('prospects');
  const { declineQuoteRequest, declineAllianceQuoteRequest } = useQuoteStore();

  const [reason, setReason] = useState('');
  const [declining, setDeclining] = useState(false);
  const [declineError, setDeclineError] = useState<string | null>(null);
  const [declineSuccess, setDeclineSuccess] = useState(false);

  const isAllianceQuoteRequest = 'parent_quote_owner' in quoteRequest;

  const navigate = useNavigate();

  const sourceProfile = isAllianceQuoteRequest
    ? quoteRequest.parent_quote_owner
    : quoteRequest.job.client;
  const clientName = sourceProfile.first_name;

  const titleKey = declineSuccess
    ? 'quoteRequestRespondModal.declinedTitle'
    : 'quoteRequestRespondModal.declineTitle';
  const messageKey = declineSuccess
    ? 'quoteRequestRespondModal.declinedTitle'
    : 'quoteRequestRespondModal.declineMessageDescription';

  const handleDecline = async () => {
    setDeclining(true);
    setDeclineError(null);
    try {
      if (isAllianceQuoteRequest) {
        await declineAllianceQuoteRequest(quoteRequest.id, reason);
      } else {
        await declineQuoteRequest(quoteRequest.id, reason);
      }
      setDeclineSuccess(true);
    } catch (error) {
      console.error(error);
      setDeclineError(t('quoteRequestRespondModal.declineError', 'Erreur lors du refus'));
    } finally {
      setDeclining(false);
    }
  };

  return (
    <>
      <h2 className="text-2xl font-bold text-secondary">{t(titleKey, 'Refuser la demande')}</h2>
      <p className="text-secondary">
        <Trans
          i18nKey={messageKey}
          ns={'prospects'}
          values={{ requesterName: clientName }}
          components={{ bold: <span className="font-bold" /> }}
        />
      </p>

      {!declineSuccess && (
        <div className="mb-4">
          <label className="mb-2 block font-bold text-secondary" htmlFor="declineReason">
            <Trans
              i18nKey={'quoteRequestRespondModal.declineReasonLabel'}
              ns={'prospects'}
              values={{ freelancerName: clientName }}
              components={{ bold: <span className="font-bold" /> }}
            />
          </label>
          <textarea
            id="declineReason"
            className="font-ambit h-24 w-full border-0 bg-[#E1E9F0] px-3 py-2 text-[0.8125rem] font-normal not-italic leading-[1.25rem]"
            value={reason}
            onChange={e => setReason(e.target.value)}
            maxLength={1000}
          />
          {declineError && (
            <div className="font-ambit mt-1 text-[0.9375rem] text-[#F04438]">{declineError}</div>
          )}
        </div>
      )}
      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
        {declineSuccess ? (
          <Button
            variant="primary"
            onClick={() => {
              navigate('/prospects', { state: { selected_id: quoteRequest.job.client.id } });
              onClick?.();
            }}
            className="w-full md:w-auto md:px-8"
          >
            {t('quoteRequestRespondModal.showNewContact', { ns: 'prospects' })}
          </Button>
        ) : (
          <>
            <Button
              type="button"
              onClick={handleDecline}
              className="w-full md:w-auto md:px-8"
              variant="primary"
              disabled={declining}
            >
              {declining
                ? t('quoteRequestRespondModal.decliningButton')
                : t('quoteRequestRespondModal.declineButton')}
            </Button>
            <Button
              type="button"
              onClick={onBack}
              className="w-full md:w-auto md:px-8"
              variant="simple-text"
            >
              {t('common:form_actions.back', { ns: 'common' })}
            </Button>
          </>
        )}
      </div>
    </>
  );
};

const QuoteRequestRespondModal: React.FC<QuoteRequestRespondModalProps> = ({
  quoteRequest,
  onClose,
}) => {
  const { t } = useTranslation('prospects');
  const [isDeclineMode, setIsDeclineMode] = useState(false);

  const [copied, setCopied] = useState(false);

  const [showReportModal, setShowReportModal] = useState(false);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[72rem]">
        <div className="flex flex-row items-center">
          <div className="absolute right-4 top-2.5 text-2xl text-gray-400 hover:text-gray-600 md:right-8">
            <ReportButtonMenu
              onReport={() => setShowReportModal(true)}
              entityOwner={quoteRequest.job.client.id}
              onCopySuccess={handleCopySuccess}
              contentTypeTitle="report_this_quote"
            />
          </div>

          <button
            className="absolute right-1 text-2xl text-gray-400 hover:text-gray-600 md:right-4 md:top-0"
            onClick={onClose}
            aria-label={t('common:form_actions.close')}
          >
            &times;
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {isDeclineMode ? (
            <QuoteDeclineContent
              quoteRequest={quoteRequest}
              onBack={() => setIsDeclineMode(false)}
              onClick={onClose}
            />
          ) : (
            <QuoteAcceptContent
              quoteRequest={quoteRequest}
              onDecline={() => setIsDeclineMode(true)}
              onClose={onClose}
            />
          )}
        </div>
      </FullBlockingModal>

      {showReportModal && quoteRequest && (
        <ReportModal
          entityId={quoteRequest.id}
          reportedId={quoteRequest.job.client.id}
          contentType="quote_request"
          onClose={() => setShowReportModal(false)}
        />
      )}

      {copied && <BottomSuccessMessage />}
    </>
  );
};

export default QuoteRequestRespondModal;
